// Import System Types and Constants

// US State Codes
export const STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Status Types
export const IMPORT_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error'
};

// Status Colors
export const STATUS_COLORS = {
  [IMPORT_STATUS.PENDING]: '#6b7280',    // Gray
  [IMPORT_STATUS.RUNNING]: '#3b82f6',    // Blue
  [IMPORT_STATUS.COMPLETED]: '#10b981',  // Green
  [IMPORT_STATUS.ERROR]: '#ef4444'       // Red
};

// Status Labels
export const STATUS_LABELS = {
  [IMPORT_STATUS.PENDING]: 'Pending',
  [IMPORT_STATUS.RUNNING]: 'Running',
  [IMPORT_STATUS.COMPLETED]: 'Completed',
  [IMPORT_STATUS.ERROR]: 'Error'
};

// Default Configuration
export const DEFAULT_CONFIG = {
  MAX_DEALERS: 100,
  VEHICLES_PER_DEALER: 100,
  MAX_STATES_PER_IMPORT: 50,
  POLLING_INTERVAL: 5000, // 5 seconds
  AUTO_REFRESH_INTERVAL: 5000 // 5 seconds
};

// Validation Rules
export const VALIDATION_RULES = {
  MAX_DEALERS: {
    MIN: 1,
    MAX: 1500
  },
  VEHICLES_PER_DEALER: {
    MIN: 1,
    MAX: 1500
  },
  MAX_STATES: 50
};

// Helper Functions
export const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export const formatDuration = (startTime) => {
  if (!startTime) return 'N/A';
  
  const start = new Date(startTime);
  const now = new Date();
  const diffMs = now - start;
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

export const calculateProgress = (current, total) => {
  if (total === 0) return 0;
  return Math.round((current / total) * 100);
};

export const getStatusIcon = (status) => {
  switch (status) {
    case IMPORT_STATUS.PENDING:
      return '⏳';
    case IMPORT_STATUS.RUNNING:
      return '🔄';
    case IMPORT_STATUS.COMPLETED:
      return '✅';
    case IMPORT_STATUS.ERROR:
      return '❌';
    default:
      return '❓';
  }
};

export const validateStateCode = (stateCode) => {
  return STATE_CODES.includes(stateCode.toUpperCase());
};

export const validateNumericInput = (value, min, max) => {
  const num = parseInt(value);
  return !isNaN(num) && num >= min && num <= max;
};

export const validateStates = (states) => {
  if (!Array.isArray(states) || states.length === 0) {
    return { isValid: false, error: 'At least one state must be selected' };
  }
  
  if (states.length > VALIDATION_RULES.MAX_STATES) {
    return { isValid: false, error: `Maximum ${VALIDATION_RULES.MAX_STATES} states allowed` };
  }
  
  const invalidStates = states.filter(state => !validateStateCode(state));
  if (invalidStates.length > 0) {
    return { isValid: false, error: `Invalid state codes: ${invalidStates.join(', ')}` };
  }
  
  const duplicates = states.filter((state, index) => states.indexOf(state) !== index);
  if (duplicates.length > 0) {
    return { isValid: false, error: `Duplicate states: ${duplicates.join(', ')}` };
  }
  
  return { isValid: true };
};
